import { useEffect } from "react";

import { useRouter, useSearchParams } from "next/navigation";

import { disposeApi } from "@/api";
import { useStore } from "@/store";
import { docPageUrl } from "@/urls";

import type { Scope } from "@/models/document/DocumentPermissionsModel";

export function useHandlePermissionQuery() {
  const { doc, auth } = useStore();
  const { permissions } = doc || {};

  const router = useRouter();
  const queryParams = useSearchParams();
  const token = queryParams.get("token");
  const accept = queryParams.get("accept");
  const permissionUid = queryParams.get("permissionUid");
  // keep username for backwards compatibility
  const username = queryParams.get("username");
  const permalink = queryParams.get("permalink") || username;
  const scope = queryParams.get("scope");

  const documentPageUrl = docPageUrl({
    docId: doc?.id || "",
  });

  useEffect(() => {
    if (!permissions || !auth.isLoggedIn) {
      return;
    }

    return disposeApi(async (signal) => {
      // TODO: open loading dialog and show a message to the user. after dialog is closed, redirect to the document page.

      if (token) {
        await permissions.acceptPermission(token);
        if (signal.aborted) return;
        router.replace(documentPageUrl);
      }

      if (permissionUid) {
        await permissions.fetchPermissionsAndRequests();
        if (signal.aborted) return;
        const matchedPermission = permissions.permissions.find(
          (permission) => permission.uid === permissionUid,
        );
        const existingPermission = permissions.permissions.find(
          (permission) => permission.confirmed && permission.team?.permalink === permalink,
        );
        if (accept === "true" && permalink && existingPermission?.scope !== scope) {
          await permissions.createPermission(
            { permalink: existingPermission?.team?.permalink ?? permalink },
            scope as Scope,
            permissionUid,
            existingPermission,
          );
        } else if (accept === "false" && matchedPermission) {
          await permissions.deletePermission(permissionUid);
        }
        if (signal.aborted) return;
        router.replace(documentPageUrl);
      }
    });
  }, [
    permissions,
    auth.isLoggedIn,
    accept,
    token,
    documentPageUrl,
    router,
    permissionUid,
    permalink,
    scope,
  ]);
}
